import { useTranslation } from 'react-i18next';
import { Badge, Button, Card } from '@parrot-software/design-system';
import { Layers, LoaderCircle, Store } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { CustomerStates, SubscriptionStatuses } from '@subscriptions/domain/types/SubscriptionsConst';
import { InactiveStatusBadge, LicenseStatusBadge, StoreCountBadge } from '@subscriptions/presentation/components/base';
import { getBlockChurnDate } from '@subscriptions/presentation/utils';
import { SubscriptionInvoiceInfo } from './SubscriptionInvoiceInfo';
import { SubscriptionPaymentInfo } from './SubscriptionPaymentInfo';
import { formatCurrencyMXN, formatPaymentDate, getLicenseBadgeVariant, getPlanSubtitle, isPromoBlock } from './utils';

/**
 * One of the decorative rectangles peeking out from under a multi-store card.
 *
 * Purely ornamental: `aria-hidden` and `pointer-events-none` so it neither captures
 * clicks on the sliver nor is announced by screen readers. The caller positions it
 * through `className` (vertical offset + lateral inset).
 */
const StackLayer = ({ className }: { className: string }) => (
  <div
    aria-hidden
    className={cn('pointer-events-none absolute rounded-2xl border border-border bg-background', className)}
  />
);

interface SubscriptionBlockCardProps {
  block: SubscriptionBlockViewDto;
  disableManage?: boolean;
  isConfiguring?: boolean;
  onManageClick: (blockUuid: string) => void;
}

/**
 * SubscriptionBlockCard - Compact card for a subscription block.
 *
 * A full-height status band runs down the left edge of every card: muted by default,
 * red (`destructive`) when the block's license is unavailable or requires payment, and
 * grey (`muted-foreground`) when the customer churned — churn wins over the error color.
 * A paid license does NOT tint the band; the header badge carries that state.
 *
 * Blocks grouping more than one store are drawn over two decorative layers peeking out
 * from under the card, reinforcing that the block bundles several stores.
 *
 * Three states:
 * - `isConfiguring`: the block's async backend configuration is running — only the
 *   identity header is shown, with a loader; all other data is hidden because it is stale.
 * - `disableManage`: another block is configuring, so this block's manage button is
 *   disabled while its data is still rendered normally.
 * - default: full data with an enabled manage button.
 */
export const SubscriptionBlockCard = ({
  block,
  disableManage = false,
  isConfiguring = false,
  onManageClick,
}: SubscriptionBlockCardProps) => {
  const { t } = useTranslation('subscriptions');

  const storeCount = block.stores.length;
  const hasStack = storeCount > 1;
  const IdentityIcon = hasStack ? Layers : Store;

  const isInactive = block.customerState === CustomerStates.INACTIVE;
  const isMissingPayment =
    block.status === SubscriptionStatuses.PAYMENT_REQUIRED || block.status === SubscriptionStatuses.UNAVAILABLE;
  const isPromo = isPromoBlock(block);
  const licenseBadge = getLicenseBadgeVariant(block, 'card');

  const actionLabel = t(isInactive ? 'card.reactivate' : 'card.manage');
  const actionVariant = isInactive ? 'destructive' : 'outline';

  const intervalBadgeLabel = isInactive
    ? t('interval.reactivateBadge')
    : isPromo
    ? t('interval.promoBadge')
    : t('interval.monthlyBadge');

  const churnDate = isInactive ? getBlockChurnDate(block) : null;

  // A churned block has no scheduled charges; only the expired line still applies.
  const paymentDateLabel = isMissingPayment || isInactive ? t('card.expiredOn') : t('card.nextPayment');
  const paymentDate = churnDate ?? block.paymentDate;
  const showPaymentDate = !isInactive || isMissingPayment || Boolean(churnDate);

  const renderStatusBadge = () => {
    if (isInactive) return <InactiveStatusBadge />;
    if (isMissingPayment) return null;
    if (isPromo) return <LicenseStatusBadge variant="paid" />;
    return licenseBadge ? <LicenseStatusBadge variant={licenseBadge} /> : null;
  };

  const renderIdentity = () => (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <div className="rounded bg-secondary p-2.5">
        <IdentityIcon aria-hidden className="h-5 w-5 text-foreground" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-base font-semibold leading-6 text-foreground">{block.name}</p>
          <StoreCountBadge count={storeCount} variant="muted" />
        </div>
        <p className="text-sm text-muted-foreground break-words md:truncate">{getPlanSubtitle(block.stores, t)}</p>
      </div>
    </div>
  );

  /** Wraps the card in the decorative multi-store stack; single-store blocks render bare. */
  const withStack = (card: JSX.Element): JSX.Element => {
    if (!hasStack) return card;

    return (
      <div className="relative pb-4">
        <StackLayer className="inset-x-5 top-4 bottom-0" />
        <StackLayer className="inset-x-2.5 top-2 bottom-2" />
        {card}
      </div>
    );
  };

  if (isConfiguring) {
    return withStack(
      <Card className="relative overflow-hidden rounded-2xl font-sans shadow-xs">
        <div className="flex">
          <div className="w-2 shrink-0 self-stretch bg-muted/50" />
          <div className="flex min-w-0 flex-1 flex-col gap-3.5 p-4">
            {renderIdentity()}

            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <LoaderCircle className="h-4 w-4 animate-spin" />
              <span>{t('configuring.cardMessage')}</span>
            </div>
          </div>
        </div>
      </Card>,
    );
  }

  return withStack(
    <Card className="relative overflow-hidden rounded-2xl font-sans shadow-xs">
      <div className="flex">
        <div
          className={cn('w-2 shrink-0 self-stretch bg-muted/50', {
            'bg-destructive': isMissingPayment && !isInactive,
            'bg-muted-foreground': isInactive,
          })}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-3.5 p-4">
          {/* Header: identity + status badge; the manage button joins the row from md up */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            {renderIdentity()}
            {renderStatusBadge()}
            <Button
              className="hidden md:inline-flex"
              disabled={disableManage}
              onClick={() => onManageClick(block.uuid)}
              variant={actionVariant}
            >
              {actionLabel}
            </Button>
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-2xl font-bold leading-9 text-foreground">{formatCurrencyMXN(block.total)}</p>
              <Badge
                className={cn('w-fit border-transparent font-semibold', {
                  'bg-muted text-muted-foreground hover:bg-muted': isInactive,
                  'bg-bg-warning text-warning hover:bg-bg-warning': isPromo && !isInactive,
                  'bg-bg-info text-info hover:bg-bg-info': !isPromo && !isInactive,
                })}
                variant="secondary"
              >
                {intervalBadgeLabel}
              </Badge>
            </div>
            {showPaymentDate && (
              <p
                className={cn('text-sm font-medium text-foreground', {
                  'font-normal text-destructive': isMissingPayment || isInactive,
                })}
              >
                {paymentDateLabel} {formatPaymentDate(paymentDate)}
                {isPromo && !isMissingPayment && !isInactive && ` · ${t('card.includesDiscounts')}`}
              </p>
            )}
          </div>

          <div className="h-px w-full bg-border" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SubscriptionInvoiceInfo advanceInvoice={block.advanceInvoice} invoiceData={block.invoiceData} />
            <SubscriptionPaymentInfo
              hasError={isMissingPayment}
              lastError={block.lastError}
              paymentMethod={block.paymentMethod}
            />
          </div>

          {/* Manage button: full-width at the foot of the card below md */}
          <div className="flex w-full md:hidden">
            <Button
              className="w-full"
              disabled={disableManage}
              onClick={() => onManageClick(block.uuid)}
              variant={actionVariant}
            >
              {actionLabel}
            </Button>
          </div>
        </div>
      </div>
    </Card>,
  );
};
